'use client';

import { Tab } from '@common/components';
import { useSearchParams } from 'next/navigation';
import {
  type CSSProperties,
  Suspense,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { DONATION_DESKTOP_STICKY_TOP } from '@/app/donation/constants';
import { BREAKPOINTS } from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useScrollToSection } from '@/hooks/useScrollToSection';

import { TABS, TABS_LABELS } from '../constants';
import type { Tab as TabType } from '../types';

interface GiftDetailTabProps {
  className?: string;
  style?: CSSProperties;
  productNumber: string;
  disabledTabs?: TabType[];
  desktopStickyTop?: number;
}

const MOBILE_STICKY_OFFSET = 48;

const GiftDetailTabComponent = ({
  className,
  style,
  disabledTabs,
  desktopStickyTop = DONATION_DESKTOP_STICKY_TOP,
}: GiftDetailTabProps) => {
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const { scrollToSectionWithTab, defaultOffset } = useScrollToSection();
  const searchParams = useSearchParams();

  const [tab, setTab] = useState<TabType>(TABS.BASIC);
  const isUserScrolling = useRef(true);

  const requestedTab = searchParams?.get('tab') as TabType | null;
  const scrollOffset = isDesktopViewport
    ? desktopStickyTop
    : MOBILE_STICKY_OFFSET;
  const sectionArrivalTop = defaultOffset + scrollOffset;

  // 초기 마운트 시 searchParams의 tab이 있으면 해당 섹션으로 스크롤
  useEffect(() => {
    if (requestedTab) {
      scrollToSectionWithTab(requestedTab, {
        behavior: 'instant',
        offset: scrollOffset,
      });
    }
  }, [scrollToSectionWithTab, requestedTab, scrollOffset]);

  // IntersectionObserver로 활성 탭 감지 (reflow 없음)
  useEffect(() => {
    const tabIds = Object.values(TABS).filter(
      (id) => !disabledTabs?.includes(id),
    );

    const visibleIds = new Set<TabType>();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const entryId = tabIds.find((id) => id === entry.target.id);
          if (!entryId) {
            continue;
          }

          if (entry.isIntersecting) {
            visibleIds.add(entryId);
          } else {
            visibleIds.delete(entryId);
          }
        }

        if (!isUserScrolling.current) return;

        //* 감지선 아래 첫 섹션이 탭 바 밑에 걸친 섹션이다 — 마지막을 고르면 짧은 섹션을 누른 뒤 다음 탭이 잡힌다
        const activeId = tabIds.find((id) => visibleIds.has(id));
        if (activeId) {
          setTab(activeId);
        }
      },
      {
        rootMargin: `-${sectionArrivalTop + 1}px 0px 0px 0px`,
        threshold: 0,
      },
    );

    tabIds.forEach((id) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [disabledTabs, sectionArrivalTop]);

  return (
    <Tab
      size={isDesktopViewport ? 'lg' : 'sm'}
      className={className}
      style={isDesktopViewport ? { top: desktopStickyTop, ...style } : style}
      variant="full"
      value={tab ?? TABS.BASIC}
      onChange={(value) => {
        isUserScrolling.current = false;
        setTab(value);
        scrollToSectionWithTab(value, { offset: scrollOffset });
        setTimeout(() => {
          isUserScrolling.current = true;
        }, 1000);
      }}
    >
      {Object.values(TABS)
        .filter((tab) => !disabledTabs?.includes(tab))
        .map((tab) => (
          <Tab.Item key={tab} value={tab}>
            {TABS_LABELS[tab]}
          </Tab.Item>
        ))}
    </Tab>
  );
};

export const GiftDetailTab = (props: GiftDetailTabProps) => {
  return (
    <ErrorBoundary fallback={null}>
      {/* 탭 바는 in-flow sticky다 — fallback이 높이 0이면 아래 콘텐츠가 위로 당겨진다. */}
      <Suspense
        fallback={
          <div className="h-70 border-b border-Gray-300 mobile-tablet:h-53" />
        }
      >
        <GiftDetailTabComponent {...props} />
      </Suspense>
    </ErrorBoundary>
  );
};
